import { z } from 'zod';
import { idSchema, isoDateSchema, isoDateTimeSchema } from '../../common.js';
/**
 * Work-schedule import (2026-09-08, "make it super easy for Kora to enter her schedule").
 *
 * Two halves, deliberately split:
 *
 * 1. **Transcription** — the model reads a photo of the restaurant's weekly shift grid and
 *    copies it verbatim: dates, section labels, the small time label above each coloured
 *    name cell (`10:30~Close`, `11:30~slow`, `5:45-close`). It never turns words into clock
 *    times. That is `shiftGridSchema`, the relay's `read-shift-grid` output contract.
 * 2. **Interpretation** — plain code (`interpret.ts`) applies the restaurant's house rules
 *    ("Close on a lunch shift means 4pm, slow means 2pm, Close on dinner means 10pm") to
 *    produce concrete events. The rules live in a per-member profile in the household doc
 *    so they can be corrected without touching a prompt.
 *
 * Keeping the model on the transcription side is what makes fidelity testable: a wrong
 * clock time is either a misread cell (visible in the review step next to the raw label)
 * or a wrong rule (a unit test).
 */
export const WEEKDAY_ABBREVIATIONS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
/** One coloured name cell and the time label directly above it. */
export const shiftGridCellSchema = z.object({
    /** Row-group label from the sheet's left edge, first word uppercased: `LUNCH`, `DINNER`. */
    section: z.string().min(1).max(40),
    /** The time label text, verbatim (`10:30~Close`, `3:30 - 7:30`, `6:30-9:30`). */
    time: z.string().min(1).max(60),
    /** The name as printed in the coloured cell. */
    name: z.string().min(1).max(60),
    /** Trailing role annotation from the label (`HOST`, `food runner&busser`), else null. */
    note: z.string().max(80).nullable(),
    /** Approximate fill colour of the name cell as one plain word (`salmon`, `yellow`). */
    color: z.string().max(30).nullable().optional(),
});
export const shiftGridDaySchema = z.object({
    date: isoDateSchema,
    weekday: z.enum(WEEKDAY_ABBREVIATIONS),
    shifts: z.array(shiftGridCellSchema).max(60),
});
export const shiftGridSchema = z.object({
    days: z.array(shiftGridDaySchema).min(1).max(14),
    /** Every distinct name on the sheet — lets the reviewer spot a misread at a glance. */
    names: z.array(z.string().min(1).max(60)).max(60),
    confidence: z.enum(['high', 'medium', 'low']),
});
// ---------------------------------------------------------------------------------------
// The per-member profile (household doc, `workScheduleProfiles` → memberId → profile)
// ---------------------------------------------------------------------------------------
export const WORK_SCHEDULE_PROFILES_KEY = 'workScheduleProfiles';
const wallClockSchema = z.string().regex(/^\d{2}:\d{2}$/, 'expected HH:MM');
/**
 * How one restaurant's sheet words map to clock times. Keys are the sheet's section
 * labels (uppercased first word); `close`/`slow` are the end-of-shift words seen so far.
 * Anything the sheet writes as a numeric range needs no rule.
 */
export const sectionRulesSchema = z.object({
    close: wallClockSchema.nullable(),
    slow: wallClockSchema.nullable(),
});
export const workScheduleProfileSchema = z.object({
    memberId: idSchema,
    /** The name as it appears on the sheet (`Kora`) — matched case-insensitively. */
    sheetName: z.string().min(1).max(60),
    /** Event title prefix; the section is appended (`Work · Lunch`). */
    title: z.string().min(1).max(100),
    rules: z.record(z.string().max(40), sectionRulesSchema),
    updatedBy: idSchema,
    updatedAt: isoDateTimeSchema,
});
/** Kora's restaurant (Dan's notes, 2026-09-08): lunch Close 4pm, lunch slow 2pm, dinner Close 10pm. */
export const DEFAULT_WORK_SCHEDULE_RULES = {
    LUNCH: { close: '16:00', slow: '14:00' },
    DINNER: { close: '22:00', slow: null },
};
export function defaultWorkScheduleProfile(input) {
    return {
        memberId: input.memberId,
        sheetName: input.sheetName,
        title: 'Work',
        rules: structuredClone(DEFAULT_WORK_SCHEDULE_RULES),
        updatedBy: input.updatedBy,
        updatedAt: (input.now ?? new Date()).toISOString(),
    };
}
export function parseWorkScheduleProfile(value) {
    const parsed = workScheduleProfileSchema.safeParse(value);
    return parsed.success ? parsed.data : null;
}
/** `importKey` stamped on every event the importer writes: `work:<memberId>:<date>`. */
export function workScheduleImportKey(memberId, date) {
    return `work:${memberId}:${date}`;
}
export const WORK_SCHEDULE_IMPORT_PREFIX = 'work:';
// ---------------------------------------------------------------------------------------
// REST contract — `POST /api/ai/read-schedule`
// ---------------------------------------------------------------------------------------
export const SCHEDULE_IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const scheduleImageMimeSchema = z.enum(SCHEDULE_IMAGE_MIME_TYPES);
/** Base64 of a ≤ 4 MB image; the client downsizes phone photos before sending. */
export const SCHEDULE_IMAGE_MAX_BASE64_CHARS = 6_000_000;
export const readScheduleRequestSchema = z.object({
    image: z.object({
        mime: scheduleImageMimeSchema,
        base64: z.string().min(1).max(SCHEDULE_IMAGE_MAX_BASE64_CHARS),
    }),
    hints: z
        .object({
        /** Whose shifts the reader cares about — lets the prompt ask for a second look. */
        person: z.string().max(60).optional(),
    })
        .optional(),
});
/** sha256 of the uploaded bytes — the archive key for the sheet and everything said about it. */
export const sheetIdSchema = z.string().regex(/^[a-f0-9]{64}$/);
/**
 * A read is a *job*, not a request/response (2026-09-09). Sonnet takes 1–4 minutes on a
 * sheet and the first real user lost two finished reads to a phone dropping the long fetch.
 * `POST` archives the photo, starts (or joins) the read, and answers right away; the client
 * polls `GET /api/ai/read-schedule/:sheetId` until the status settles. Both return this.
 *
 *   reading — in flight; keep polling
 *   done    — the verbatim grid, exactly what the synchronous response used to carry
 *   napping — the relay (or `claude` behind it) couldn't do it; the photo is kept
 *   failed  — the model answered but not in a usable shape; a clearer photo usually fixes it
 */
export const readScheduleStatusSchema = z.discriminatedUnion('status', [
    z.object({ status: z.literal('reading'), sheetId: sheetIdSchema, startedAt: isoDateTimeSchema }),
    z.object({
        status: z.literal('done'),
        sheetId: sheetIdSchema,
        grid: shiftGridSchema,
        attempts: z.number().int().min(1).max(2).optional(),
    }),
    z.object({ status: z.literal('napping'), sheetId: sheetIdSchema }),
    z.object({ status: z.literal('failed'), sheetId: sheetIdSchema, detail: z.string().max(400) }),
]);
/** What `POST /api/ai/read-schedule` and `GET /api/ai/read-schedule/:sheetId` both answer. */
export const readScheduleResponseSchema = readScheduleStatusSchema;
// ---------------------------------------------------------------------------------------
// REST contract — `POST /api/ai/schedule-feedback` ("hey, this wasn't quite right")
// ---------------------------------------------------------------------------------------
/**
 * Every correction made in the review step, plus any free-text note, is appended beside the
 * archived sheet on the server. Nothing is ever asked of the user for it — fixing a time
 * *is* the feedback. A later session tunes the prompt against real misses, not guesses.
 */
export const scheduleCorrectionSchema = z.object({
    date: isoDateSchema,
    section: z.string().max(40),
    label: z.string().max(60),
    transcribed: z.object({ start: wallClockSchema.nullable(), end: wallClockSchema.nullable() }),
    /** null = "this isn't my shift" (unticked); otherwise the times the person settled on. */
    corrected: z.object({ start: wallClockSchema, end: wallClockSchema }).nullable(),
    reason: z.enum(['not-mine', 'wrong-time', 'unreadable', 'missing']).optional(),
});
export const scheduleFeedbackRequestSchema = z.object({
    sheetId: sheetIdSchema,
    memberId: idSchema,
    sheetName: z.string().max(60),
    corrections: z.array(scheduleCorrectionSchema).max(100).default([]),
    comment: z.string().max(2000).optional(),
    /** How many shifts were written to the calendar in the end. */
    imported: z.number().int().min(0).max(100).optional(),
});
//# sourceMappingURL=schema.js.map